/**
 * CID Code Service
 * API integration for CID-10 codes CRUD operations
 *
 * @version 2025.1.0
 * @description Serviço de integração com API de códigos CID-10
 */
import apiClient from './apiClient';
import {
  CidCode,
  CidCodeFilters,
  CidCodeCreateFormData,
  CidCodeUpdateFormData,
  CidCodeListResponse,
  CidCodeStatistics,
  CidCategory,
  getCidCategoryOptions,
} from '../types/cidCode';

// Re-export types for convenience
export * from '../types/cidCode';

export interface CidCodeListParams extends CidCodeFilters {
  page?: number;
  limit?: number;
}

class CidCodeService {
  private baseUrl = '/cid-codes';

  /**
   * Get paginated list of CID codes
   */
  async getCidCodes(params: CidCodeListParams = {}): Promise<CidCodeListResponse> {
    const { page = 1, limit = 25, ...filters } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters.search) queryParams.append('search', filters.search);
    if (filters.code) queryParams.append('code', filters.code);
    if (filters.category) queryParams.append('category', filters.category);
    if (filters.subcategory) queryParams.append('subcategory', filters.subcategory);
    if (filters.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`) as {
      message: string;
      data: CidCode[];
      pagination?: { total: number; page: number; limit: number; totalPages: number };
    };

    return {
      cidCodes: response.data ?? [],
      total: response.pagination?.total ?? 0,
      page: response.pagination?.page || page,
      limit: response.pagination?.limit || limit,
      totalPages: response.pagination?.totalPages || Math.ceil((response.pagination?.total ?? 0) / limit),
    };
  }

  /**
   * Get CID code by ID
   */
  async getCidCodeById(id: string | number): Promise<CidCode> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  /**
   * Create new CID code
   */
  async createCidCode(data: CidCodeCreateFormData): Promise<CidCode> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data.data || response.data;
  }

  /**
   * Update CID code by ID
   */
  async updateCidCode(id: string | number, data: CidCodeUpdateFormData): Promise<CidCode> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data.data || response.data;
  }

  /**
   * Delete CID code by ID
   */
  async deleteCidCode(id: string | number, hardDelete: boolean = false): Promise<void> {
    const queryParams = hardDelete ? '?hardDelete=true' : '';
    await apiClient.delete(`${this.baseUrl}/${id}${queryParams}`);
  }

  /**
   * Deactivate CID code (soft delete)
   */
  async deactivateCidCode(id: string | number): Promise<CidCode> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/deactivate`);
    return response.data.data || response.data;
  }

  /**
   * Reactivate CID code
   */
  async reactivateCidCode(id: string | number): Promise<CidCode> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/reactivate`);
    return response.data.data || response.data;
  }

  /**
   * Get catalog statistics
   */
  async getStatistics(category?: string): Promise<CidCodeStatistics> {
    const queryParams = category ? `?category=${category}` : '';
    const response = await apiClient.get(`${this.baseUrl}/statistics${queryParams}`);
    return response.data.data || response.data;
  }

  /**
   * Get all categories from API
   */
  async getCategories(): Promise<CidCategory[]> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/categories`);
      return response.data.data || response.data;
    } catch {
      // Fallback to local constants if API fails
      return getCidCategoryOptions();
    }
  }

  /**
   * Search CID codes by description
   */
  async searchByDescription(searchTerm: string, limit: number = 50): Promise<CidCode[]> {
    const queryParams = new URLSearchParams({
      q: searchTerm,
      limit: limit.toString(),
    });
    const response = await apiClient.get(`${this.baseUrl}/search?${queryParams.toString()}`);
    return response.data.data || response.data;
  }

  /**
   * Get CID codes by category
   */
  async getByCategory(category: string, activeOnly: boolean = true): Promise<CidCode[]> {
    const queryParams = activeOnly ? '' : '?activeOnly=false';
    const response = await apiClient.get(`${this.baseUrl}/category/${encodeURIComponent(category)}${queryParams}`);
    return response.data.data || response.data;
  }

  /**
   * Get category options for select dropdowns (local)
   */
  getCategoryOptions(): Array<{ value: string; label: string }> {
    return getCidCategoryOptions().map(cat => ({
      value: cat.code,
      label: `${cat.code} - ${cat.description}`
    }));
  }

  /**
   * Export CID codes to Excel
   */
  async exportCidCodes(): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/export`, {
      responseType: 'blob',
    });
    return response as unknown as Blob;
  }

  /**
   * Download import template
   */
  async downloadTemplate(): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/template`, {
      responseType: 'blob',
    });
    return response as unknown as Blob;
  }

  /**
   * Import CID codes from Excel file
   */
  async importCidCodes(file: File): Promise<{ imported: number; skipped: number; errors: Array<{ row: number; error: string }> }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${this.baseUrl}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return (response as any).data ?? response;
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get(`${this.baseUrl}/health`);
    return response.data.data || response.data;
  }
}

// Export singleton instance
export const cidCodeService = new CidCodeService();
export default cidCodeService;
